import React from 'react';
import moment from 'moment';

import { Avatar, FileBox, MCButton, MCText, View } from 'src/components/basic';
import { remCss, remFontCss } from 'src/components/mobile/utils';
import { ActivityLabel } from 'src/components/user/LastActivity';
import LANG_CONST from 'src/helpers/lang/const';
import {
  lang,
  scssVariables as variables,
  number,
  userFullName,
  useScreenDetail
} from 'src/helpers';
import DefaultPdfImg from 'src/assets/images/default-pdf-img.png';
import { MCDatePicker } from '../complex';
import { StaticGoogleMap } from '../location/GoogleMaps';

import { useDoctorDetail } from '../doctor/useDoctorDetail';
import { LocationList, SpecialistChips, SubSpeciality } from '../doctor/DoctorDetailParts';

const Section = ({ title, children }) => (
  <>
    <MCText label={title} size={20} weight={600} style={{ marginTop: remCss(5) }} />
    <View style={{ gap: remCss(15) }}>{children}</View>
  </>
);

// Layout only. All data logic is in useDoctorDetail.
export const DoctorDetail = ({
  doctor,
  hospitalName,
  locations = [],
  selectedDate,
  selectedDay,
  selectedMonth,
  slotsMonth,
  onBook,
  onSendMsg
}) => {
  const screen = useScreenDetail();
  const d = useDoctorDetail({ doctor, locations, slotsMonth });

  return (
    <View
      style={{
        overflow: 'auto',
        paddingHorizontal: remCss(15),
        gap: remCss(20),
        paddingBottom: remCss(15)
      }}
    >
      <Avatar
        text={doctor.firstName}
        width={remCss(200)}
        height={remCss(200)}
        image={doctor.avatar}
        onlineState={doctor.connections > 0 ? 'online' : ''}
      />

      <View
        style={{
          gap: remCss(15),
          borderBottomWidth: 1,
          borderBottomColor: variables['colors-mc-border2'],
          borderBottomStyle: 'solid',
          paddingBottom: remCss(15)
        }}
      >
        <View style={{ flex: 1, gap: remCss(5) }}>
          <View style={{ flexDirection: 'row', gap: remCss(15), alignItems: 'center' }}>
            <MCText label={userFullName(doctor, true)} size={remFontCss(24)} weight={600} flex />
            <View>
              <MCButton
                label={LANG_CONST.buttons.make_appointment}
                primary
                largeMedium
                dark
                radius
                onClick={onBook}
              />
            </View>
          </View>
          <MCText
            size={remFontCss(20)}
            weight={400}
            textLight
            label={LANG_CONST.constants.doctor_types[doctor.doctorType]}
          />
          <MCText size={remFontCss(18)} numberOfLines={1} weight={400} primary>
            <ActivityLabel
              accountState={doctor.accountState}
              lastActivity={doctor.lastActivity}
              connections={doctor.connections}
            />
          </MCText>
        </View>
        {d.chatTicketLabel && (
          <MCText label={d.chatTicketLabel} size={remFontCss(20)} weight={700} />
        )}
        <View style={{ alignItems: 'center' }}>
          <MCButton
            onClick={onSendMsg}
            primary
            largeMedium
            dark
            radius
            label={LANG_CONST.buttons.send_a_message}
          />
        </View>
      </View>

      <View>
        <View style={{ paddingBottom: remCss(20) }}>
          <SpecialistChips specialist={doctor.specialist} fontSize={remFontCss} />
        </View>
        <SubSpeciality value={doctor.subSpeciality} fontSize={remFontCss} />
      </View>

      <View style={{ gap: remCss(10) }}>
        <MCText label={LANG_CONST.total_experience} size={18} weight={400} textLight />
        <MCText label={d.experience} size={18} weight={500} />
      </View>

      {doctor.description && <MCText label={doctor.description} size={18} weight={400} textLight />}

      {!!doctor.certificateFiles?.length && (
        <Section title={LANG_CONST.titles.certificates}>
          {doctor.certificateFiles.map((item, i) => (
            <View key={i} style={{ flexDirection: 'row', gap: remCss(15), alignItems: 'center' }}>
              <View style={{ height: remCss(70), width: remCss(70) }}>
                <FileBox
                  template={1}
                  thumbnail={DefaultPdfImg}
                  file={{
                    ...item,
                    name: `${item.title} (${lang.t(LANG_CONST.constants.specialist[item.specialty])} - ${item.promotionYear})`
                  }}
                  showFileName={false}
                  onClose={null}
                  styles={{ height: remCss(70), width: remCss(70) }}
                />
              </View>
              <View>
                <MCText label={item.title} size={18} weight={400} textLight />
                <MCText label={LANG_CONST.constants.specialist[item.specialty]} size={18} weight={500} />
                <MCText label={item.promotionYear} size={18} weight={400} />
              </View>
            </View>
          ))}
        </Section>
      )}

      {!!doctor.treatments?.length && (
        <Section title={LANG_CONST.titles.treatments}>
          {doctor.treatments.map((item, i) => (
            <View key={i} style={{ flexDirection: 'row', gap: remCss(15), alignItems: 'center' }}>
              <MCText label={item.name || item.title} size={18} weight={400} textLight flex />
              <MCText
                label={`${number.format(item.fee || item.amount || 0)} ${d.currency}`}
                size={18}
                weight={500}
              />
            </View>
          ))}
        </Section>
      )}

      <View style={{ flexDirection: 'row' }}>
        <MCButton
          label={LANG_CONST.buttons.make_appointment}
          primary
          medium
          dark
          radius
          onClick={onBook}
        />
      </View>

      <MCDatePicker
        style={{ padding: 0, maxWidth: 380 }}
        selected={selectedDate}
        isSMedium={screen.innerWidth >= 380}
        isSmall={screen.innerWidth < 380}
        monthStyle={{ justifyContent: 'space-between', margin: `0 0 ${remCss(20)}px 0` }}
        minDate={moment()}
        onChange={selectedDay}
        onSetMonth={selectedMonth}
        onClassNamesOfCell={d.onClassNamesOfCell}
      />

      {(hospitalName || !!d.locations.length) && (
        <MCText label={LANG_CONST.work_location} size={20} weight={600} />
      )}
      {hospitalName && (
        <View style={{ gap: remCss(15) }}>
          <MCText label={LANG_CONST.hospital_name} size={18} weight={400} textLight />
          <MCText label={hospitalName} size={18} weight={500} />
        </View>
      )}
      {!!d.locations.length && (
        <View style={{ gap: remCss(15) }}>
          <MCText label={LANG_CONST.location} size={18} weight={400} textLight />
          <LocationList
            locations={d.locations}
            selected={d.selectedLocation}
            onSelect={d.selectLocation}
          />
          {d.selectedLocation && (
            <StaticGoogleMap
              lat={d.selectedLocation.place?.location?.latitude}
              lng={d.selectedLocation.place?.location?.longitude}
            />
          )}
        </View>
      )}
    </View>
  );
};
